import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@repo/ui/components/button';
import { FormError } from '@repo/ui/components/form-error';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useLogin } from '@/hooks/mutations/use-login';
import { useCheckUserExists } from '@/hooks/queries/use-check-user-exists';

const emailSchema = z.object({
  email: z
    .string()
    .min(1, 'O email é obrigatório')
    .email('Digite um email válido'),
});

type EmailFormValues = z.infer<typeof emailSchema>;

interface EmailStepProps {
  creatorName: string;
  onExistingUser: (email: string) => void;
  onNewUser: (email: string) => void;
}

export function EmailStep({
  creatorName,
  onExistingUser,
  onNewUser,
}: EmailStepProps) {
  const [isChecking, setIsChecking] = useState(false);
  const loginMutation = useLogin();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: '' },
  });

  const email = watch('email');

  const checkUserExistsQuery = useCheckUserExists(email, { enabled: false });

  const onSubmit = async (data: EmailFormValues) => {
    setIsChecking(true);
    try {
      const result = await checkUserExistsQuery.refetch();

      if (result.data?.exists) {
        // User exists - send magic link
        await loginMutation.mutateAsync({ email: data.email });
        toast.success('Link de acesso enviado para seu email');
        onExistingUser(data.email);
      } else {
        // New user - continue onboarding
        onNewUser(data.email);
      }
    } catch {
      toast.error('Erro ao verificar email. Tente novamente.');
    } finally {
      setIsChecking(false);
    }
  };

  const isPending = isChecking || loginMutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          Aceite o convite de {creatorName}
        </h1>
        <p className="text-muted-foreground text-sm">
          Digite seu email para continuar
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="seu@email.com"
          autoComplete="email"
          autoFocus
          disabled={isPending}
          {...register('email')}
        />
        <FormError message={errors.email?.message} />
      </div>

      <Button type="submit" className="w-full" disabled={isPending}>
        {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Continuar
      </Button>
    </form>
  );
}
